import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui'
import { useEffect, useMemo, useState } from 'react'

const DEFAULT_FONT = 'Open Sans'

export const customFonts = [
  'Alexandria',
  'Montserrat',
  'Roboto',
  'Roboto Slab',
  'Space Grotesk',
  'Inter',
  'Poppins',
] as const

const supportedFonts = [DEFAULT_FONT, ...customFonts] as const

const supportedFontSpecs: {
  [K in (typeof customFonts)[number]]: string
} = {
  Montserrat: 'ital,wght@0,100..900;1,100..900',
  'Roboto Slab': 'wght@100..900',
  'Space Grotesk': 'wght@100..900',
  Alexandria: 'wght@100..900',
  Roboto: 'ital,wght@0,100..900;1,100..900',
  Inter: 'ital,wght@0,100..900;1,100..900',
  Poppins:
    'ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900',
}

// Type guard for supported font
export const isSupportedFont = (font: string | null): font is (typeof supportedFonts)[number] =>
  supportedFonts.includes(font as any)

export const FontPicker = ({
  value,
  onValueChange,
  disabled,
  className,
}: {
  value: (typeof customFonts)[number] | string | null
  onValueChange: (
    newValue:
      | { fontFamily: null }
      | { fontFamily: (typeof customFonts)[number]; fontFamilySpec: string },
  ) => void
  disabled?: boolean
  className?: string
}) => {
  const [internalValue, setInternalValue] = useState<(typeof supportedFonts)[number] | string>(
    DEFAULT_FONT,
  )

  useEffect(() => {
    setInternalValue(value || DEFAULT_FONT)
  }, [value])

  // If using unsupported font, then make it the only available option. This is the case for
  // hardcoded hosted fonts - it's not possible to select a different font in this case.
  const allFonts = useMemo(
    () => (value && !isSupportedFont(value) ? [value] : supportedFonts),
    [value],
  )

  return (
    <Select
      disabled={disabled}
      value={internalValue}
      onValueChange={(newValue) => {
        if (newValue === DEFAULT_FONT) {
          onValueChange({
            fontFamily: null,
          })
          return
        }
        onValueChange({
          fontFamily: newValue as (typeof customFonts)[number],
          fontFamilySpec: supportedFontSpecs[newValue as (typeof customFonts)[number]],
        })

        setInternalValue(newValue as (typeof supportedFonts)[number])
      }}
    >
      <SelectTrigger className={className}>
        <SelectValue placeholder='Select a font family' />
      </SelectTrigger>
      <SelectContent>
        {allFonts.map((font) => (
          <SelectItem key={font} value={font}>
            {font}
            {font === DEFAULT_FONT && <span className={'text-grey-s4 ml-1'}>(default)</span>}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
